import {
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react"
import { createPortal } from "react-dom"
import { DndProvider } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import { Loader2, Search } from "lucide-react"
import type { Client } from "@xmpp/client"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { extractVhostFromJid } from "@/jidUtils"
import { createDraft } from "@/lib/dailyRoutes/draft"
import { compact, norm } from "@/lib/dailyRoutes/names"
import {
  discardDraftRoute,
  editDraft,
  getSession,
  hasDirtyRoute,
  resetDailyRoutesCache,
  setLoadedDraft,
  startSave,
  subscribeSessions,
} from "@/lib/dailyRoutes/session"
import { loadRoutesData } from "@/lib/dailyRoutes/storage"
import { XmppContext } from "@/stream/xmppClient"
import { RouteSection } from "./RouteSection"
import { useTranslation } from "react-i18next"

// Draft, save in flight and errors live in the session module, so switching
// tabs never loses edits. Only closing or reloading the browser tab asks for
// confirmation.
export { resetDailyRoutesCache }

type LoadStatus = "loading" | "error" | "ready"

const codeMatches = (code: string, query: string): boolean =>
  norm(code).includes(norm(query))

const nameMatches = (route: string, query: string): boolean => {
  const squashed = compact(query)
  return squashed !== "" && compact(route).includes(squashed)
}

// keyed by vhost: a different vhost starts from a clean screen
export default function DailyRoutes() {
  const { client } = useContext(XmppContext)
  const vhost = client.jid ? extractVhostFromJid(client.jid.toString()) : ""
  return <DailyRoutesScreen key={vhost} client={client} vhost={vhost} />
}

function DailyRoutesScreen({
  client,
  vhost,
}: {
  client: Client
  vhost: string
}) {
  const { t } = useTranslation()
  const session = useSyncExternalStore(subscribeSessions, () =>
    getSession(vhost),
  )
  const { draft, errors } = session
  const saving = session.saving !== null

  // a draft with edits (or a save still running) is kept; a clean one may be
  // stale, so it is fetched again
  const [status, setStatus] = useState<LoadStatus>(() =>
    hasDirtyRoute(getSession(vhost).draft) ? "ready" : "loading",
  )
  const [attempt, setAttempt] = useState(0)
  const [query, setQuery] = useState("")
  // manual open/close, wins over the search's auto-expansion
  const [toggled, setToggled] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (hasDirtyRoute(getSession(vhost).draft)) return
    let cancelled = false
    setStatus("loading")
    loadRoutesData(client)
      .then((data) => {
        if (cancelled) return
        setLoadedDraft(vhost, createDraft(data))
        setStatus("ready")
      })
      .catch(() => {
        if (!cancelled) setStatus("error")
      })
    return () => {
      cancelled = true
    }
  }, [client, vhost, attempt])

  const anyDirty = hasDirtyRoute(draft)

  useEffect(() => {
    if (!anyDirty) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", warn)
    return () => window.removeEventListener("beforeunload", warn)
  }, [anyDirty])

  const visible = useMemo(() => {
    if (!draft) return []
    const q = query.trim()
    const autoExpand = norm(q).length >= 2
    return Object.keys(draft.orders).flatMap((route) => {
      if (q === "") return [{ route, auto: false }]
      const byCode = draft.orders[route].some((code) => codeMatches(code, q))
      if (!byCode && !nameMatches(route, q)) return []
      return [{ route, auto: byCode && autoExpand }]
    })
  }, [draft, query])

  const renderBody = () => {
    if (status === "loading") {
      return (
        <div className="flex justify-center py-12">
          <Loader2
            size={25}
            className="animate-spin"
            role="status"
            aria-label={t("maps.dailyRoutes.loading")}
          />
        </div>
      )
    }
    if (status === "error" || !draft) {
      return (
        <div className="flex flex-col items-start gap-3">
          <p>{t("maps.dailyRoutes.loadFailed")}</p>
          <Button type="button" onClick={() => setAttempt((n) => n + 1)}>
            {t("common.retry")}
          </Button>
        </div>
      )
    }
    if (Object.keys(draft.orders).length === 0) {
      return <p>{t("maps.dailyRoutes.noRoutes")}</p>
    }
    return (
      <>
        <div className="relative mb-4 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label={t("maps.dailyRoutes.search")}
            placeholder={t("maps.dailyRoutes.search")}
            className="pl-9"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              setToggled({})
            }}
          />
        </div>
        <DndProvider backend={HTML5Backend}>
          <div className="flex flex-col gap-3">
            {visible.map(({ route, auto }) => {
              const expanded = toggled[route] ?? auto
              return (
                <RouteSection
                  key={route}
                  route={route}
                  draft={draft}
                  expanded={expanded}
                  saving={saving}
                  error={errors[route] ?? null}
                  onToggle={() =>
                    setToggled((current) => ({
                      ...current,
                      [route]: !expanded,
                    }))
                  }
                  onChange={(next) => editDraft(vhost, next)}
                  onSave={() => void startSave(client, vhost, route)}
                  onDiscard={() => discardDraftRoute(vhost, route)}
                />
              )
            })}
          </div>
        </DndProvider>
      </>
    )
  }

  return createPortal(
    <div className="fixed inset-0 left-[49px] z-40 overflow-auto bg-background">
      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">
          {t("maps.dailyRoutes.title")}
        </h1>
        {renderBody()}
      </div>
    </div>,
    document.body,
  )
}
